import { Alert, LearnMoreLink, Modal, Text } from '@ui/themed';
import { t } from 'i18next';
import z from 'zod';
import type { Setting } from '../../../../context/settings-context';
import { useSettings } from '../../../../context/settings-context';
import type { I18nPrefix } from '../../../../i18n/prefix';
import { useAppForm } from '../../../../lib/app-form';
import { PARTNER_DOCS_URLS } from '../../constants';
import type { PartnerModalProps } from '../../types';
import { CredentialField } from '../credential-field';
import { usePartnerModalSave } from './shared';

const INTUITIVE = 'features:partners.components.intuitive_shipping' satisfies I18nPrefix;
const COMMON = 'features:partners.components.common' satisfies I18nPrefix;

export function IntuitiveShippingModal({ isOpen, onClose }: PartnerModalProps) {
  const settings = useSettings();
  const { save, isSaving } = usePartnerModalSave(onClose);

  const hasStoredKey = !!settings.integrationIntuitiveShippingApiKey;
  const apiKeyValidator = hasStoredKey ? z.string() : z.string().trim().min(1);

  const form = useAppForm({
    defaultValues: { apiKey: '' },
    validators: { onSubmit: z.object({ apiKey: apiKeyValidator }) },
    onSubmit: ({ value }) => {
      const payload: Setting[] = [{ key: 'integrationIntuitiveShipping', value: true }];
      const apiKey = value.apiKey.trim();
      if (apiKey) {
        payload.push({ key: 'integrationIntuitiveShippingApiKey', value: apiKey });
      }
      void save(payload);
    },
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      secondaryAction={{ text: t(`${COMMON}.close`), onClick: onClose }}
      primaryAction={{
        text: t(`${COMMON}.save`),
        onClick: () => void form.handleSubmit(),
        loading: isSaving,
      }}
    >
      <Modal.Header>Intuitive Shipping</Modal.Header>
      <Modal.Body>
        <div className="flex flex-col gap-4">
          {!settings.useInternalShipping && (
            <Alert variant="warning" headingLevel="h2">
              {t(`${INTUITIVE}.enable_sparklayer_shipping_alert`)}
            </Alert>
          )}
          <Text variant="secondary">
            {t(`${INTUITIVE}.description`)}{' '}
            <LearnMoreLink link={PARTNER_DOCS_URLS.intuitiveShipping} />
          </Text>
          <form.Field name="apiKey">
            {(field) => (
              <CredentialField
                label={t(`${INTUITIVE}.api_key_label`)}
                placeholder={t(`${INTUITIVE}.api_key_placeholder`)}
                tooltip={t(`${INTUITIVE}.api_key_tooltip`)}
                value={field.state.value}
                onChange={field.handleChange}
                hasStoredValue={hasStoredKey}
                error={field.state.meta.errors[0]?.message}
              />
            )}
          </form.Field>
        </div>
      </Modal.Body>
    </Modal>
  );
}
